import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { useLeadtimeAnalysis } from '../hooks/useLeadtimeAnalysis';
import { useCoverage } from '../hooks/useCoverage';
import { KNOWN_SERVICE_CODES } from '../orders.constants';
import { formatLeadTime } from '../format';

export function OrderAnalyticsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceCode = searchParams.get('serviceCode') ?? KNOWN_SERVICE_CODES[0];
  const from = searchParams.get('from') ?? undefined;
  const to = searchParams.get('to') ?? undefined;

  const coverageQuery = useCoverage();
  const leadtimeQuery = useLeadtimeAnalysis(serviceCode, { from, to });

  function setServiceCode(nextServiceCode: string) {
    const next = new URLSearchParams(searchParams);
    next.set('serviceCode', nextServiceCode);
    setSearchParams(next);
  }

  function setWindowField(field: 'from' | 'to', value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete(field);
    else next.set(field, value);
    setSearchParams(next);
  }

  const coverageRows = coverageQuery.data?.data ?? [];
  const coverageDegraded = coverageQuery.data?.meta.degraded ?? false;
  const transitions = leadtimeQuery.data?.data?.transitions ?? [];
  const leadtimeDegraded = leadtimeQuery.data?.meta.degraded ?? false;

  if (leadtimeQuery.isError) {
    return <ApiErrorMessage error={leadtimeQuery.error} onRetry={() => leadtimeQuery.refetch()} />;
  }

  return (
    <div className="space-y-6" data-testid="order-analytics-page">
      <header>
        <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('orders.analytics.title')}</h1>
      </header>

      <section aria-label={t('orders.analytics.coverageSectionLabel')} data-testid="coverage-section">
        <h2 className="mb-2 text-sm font-semibold text-[var(--foreground)]">{t('orders.analytics.coverageSectionLabel')}</h2>
        {coverageDegraded ? (
          <p className="text-sm text-[var(--foreground-muted)]" data-testid="coverage-degraded">{t('orders.analytics.coverageDegraded')}</p>
        ) : (
          <Table data-testid="coverage-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('orders.analytics.table.serviceCode')}</TableHead>
                <TableHead className="text-right">{t('orders.analytics.table.observed')}</TableHead>
                <TableHead className="text-right">{t('orders.analytics.table.expected')}</TableHead>
                <TableHead className="text-right">{t('orders.analytics.table.coveragePercent')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coverageRows.map((row) => (
                <TableRow data-testid={`coverage-row-${row.serviceCode}`} key={row.serviceCode}>
                  <TableCell className="font-medium text-[var(--foreground)]">{row.serviceCode}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.observedTradeItems}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.expectedTradeItems}</TableCell>
                  <TableCell className="text-right tabular-nums">{t('orders.analytics.table.percentValue', { value: row.coveragePercent })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      <section aria-label={t('orders.analytics.leadtimeSectionLabel')} data-testid="leadtime-section">
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-[var(--foreground-muted)]">
            {t('orders.filters.serviceCode')}
            <select
              aria-label={t('orders.filters.serviceCode')}
              className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
              onChange={(event) => setServiceCode(event.target.value)}
              value={serviceCode}
            >
              {KNOWN_SERVICE_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-[var(--foreground-muted)]">
            {t('orders.filters.from')}
            <input
              aria-label={t('orders.analytics.windowFrom')}
              className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
              onChange={(event) => setWindowField('from', event.target.value)}
              type="date"
              value={from ?? ''}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[var(--foreground-muted)]">
            {t('orders.filters.to')}
            <input
              aria-label={t('orders.analytics.windowTo')}
              className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
              onChange={(event) => setWindowField('to', event.target.value)}
              type="date"
              value={to ?? ''}
            />
          </label>
        </div>

        {leadtimeQuery.isLoading ? (
          <div className="space-y-2" data-testid="leadtime-loading">
            {Array.from({ length: 3 }).map((_, index) => <div className="skeleton h-8 rounded-lg" key={index} />)}
          </div>
        ) : leadtimeDegraded ? (
          <p className="text-sm text-[var(--foreground-muted)]" data-testid="leadtime-degraded">{t('orders.analytics.leadtimeDegraded')}</p>
        ) : transitions.length === 0 ? (
          <p className="text-sm text-[var(--foreground-muted)]" data-testid="leadtime-empty">{t('orders.analytics.leadtimeEmpty')}</p>
        ) : (
          <Table data-testid="leadtime-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('orders.analytics.table.transition')}</TableHead>
                <TableHead className="text-right">{t('orders.analytics.table.observations')}</TableHead>
                <TableHead className="text-right">{t('orders.analytics.table.average')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transitions.map((transition) => (
                <TableRow data-testid={`transition-row-${transition.from}-${transition.to}`} key={`${transition.from}-${transition.to}`}>
                  <TableCell className="text-[var(--foreground)]">{t('orders.analytics.table.transitionLabel', { from: transition.from, to: transition.to })}</TableCell>
                  <TableCell className="text-right tabular-nums">{transition.observations}</TableCell>
                  <TableCell className="text-right">
                    {transition.insufficientData || transition.averageMs == null
                      ? <span className="text-[var(--foreground-muted)]">{t('orders.analytics.insufficientData')}</span>
                      : formatLeadTime(transition.averageMs, t)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
    </div>
  );
}
